import { Controller } from '@hotwired/stimulus';

/**
 * Form Auto Submit Controller
 *
 * Automatically submits a form when clicking outside of it or when specific events occur.
 * This is useful for forms that should submit immediately when user interaction changes
 * (like dropdown selections) without requiring an explicit submit button.
 *
 * Usage:
 *
 * HTML structure:
 * ```html
 * <form data-controller="form-auto-submit">
 *   <select data-action="change->form-auto-submit#submit">
 *     <option value="1">Option 1</option>
 *     <option value="2">Option 2</option>
 *   </select>
 * </form>
 * ```
 *
 * The controller automatically submits the form when:
 * - User clicks outside the form element
 * - Specific events trigger the submit action (e.g., change events on form controls)
 *
 * Actions:
 * - submit(): Manually triggers form submission
 *
 * Example with Turbo Stream:
 * ```slim
 * = simple_form_for @interaction, url: ai_emails_category_statistic_path(@interaction), method: :put,
 *                   data: { controller: "form-auto-submit" } do |form|
 *   = form.select :human_category_id, @page.category_options, {},
 *                 class: "form-control form-control-sm form-select form-select-sm",
 *                 data: { action: "change->form-auto-submit#submit" }
 * ```
 */
// Connects to data-controller="form-auto-submit"
export default class extends Controller {
  connect() {
    this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
    document.addEventListener('click', this.boundHandleDocumentClick);
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleDocumentClick);
  }

  submit() {
    this.element.requestSubmit();
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.submit();
    }
  }
}
